import { Button } from '@rulebooks/evidend-titan';
import { Trans } from '@lingui/macro';
import { LoaderIcon } from 'components/LoaderIcon/LoaderIcon';
import { useMutationFeeMessage } from 'components/MutationFee/MutationFeeNotice';
import { useWizardContext, WizardFooter, WizardHeader } from 'components/Wizard';
import { useExecuteMutationForm } from 'forms/mutation/useExecuteMutationForm';
import { useMutationFeeConfirmation } from 'hooks/useMutationFee';
import { useMutationQueryProvider } from 'mutations/providers/MutationQueryProvider';
import React, { useCallback } from 'react';
import { FormProvider } from 'react-hook-form';

interface IWizardSummary {
  children?: React.ReactNode;
}

export const WizardSummary = ({ children }: IWizardSummary) => {
  const { previousStep } = useWizardContext();
  const [mutation] = useMutationQueryProvider();
  const [mutationForm, { handleMutationSubmit, labels }] = useExecuteMutationForm();
  const {
    formState: { isSubmitting },
  } = mutationForm;

  const { confirmFee, feeConfirmDialog } = useMutationFeeConfirmation({ price: mutation.price });

  // The fee is part of the footer instruction here; falling back keeps the footer from going blank
  // when the price is unavailable.
  const feeMessage = useMutationFeeMessage(mutation.price);

  // Saving the summary finalises the mutation, which is the moment it is charged.
  const onSubmit = useCallback(async () => {
    if (!(await confirmFee())) return;
    handleMutationSubmit();
  }, [confirmFee, handleMutationSubmit]);

  return (
    <FormProvider {...mutationForm}>
      <WizardHeader previousStep={previousStep} title={labels.header}>
        {labels.introduction}
      </WizardHeader>

      {children}

      <WizardFooter isLoading={isSubmitting} label={feeMessage ?? labels.footer} labelAlign="left">
        <Button
          iconLeft={isSubmitting ? <LoaderIcon /> : undefined}
          isDisabled={isSubmitting}
          variant="primary"
          onClick={onSubmit}
        >
          <Trans>Bevestigen</Trans>
        </Button>
      </WizardFooter>

      {feeConfirmDialog}
    </FormProvider>
  );
};
